export type KioskLanguage = "gl" | "es" | "en";

export const LANGUAGE_STORAGE_KEY = "lugo-kiosk-language";

export const isKioskLanguage = (value: string | null): value is KioskLanguage =>
  value === "gl" || value === "es" || value === "en";

type RatingLabels = [string, string, string, string, string];

interface KioskTranslations {
  loading: string;
  surveyTitle: string;
  kiosk: string;
  experienceQuestion: string;
  experienceHelp: string;
  ratings: RatingLabels;
  originQuestion: string;
  originHelp: string;
  spain: string;
  europe: string;
  america: string;
  otherCountry: string;
  postalCodeTitle: string;
  postalCodeHelp: string;
  postalCodeError: string;
  delete: string;
  send: string;
  thanksTitle: string;
  thanksMessage: string;
  returningHome: string;
  returnNow: string;
  municipalityMissing: string;
  errorTitle: string;
  submitErrorTitle: string;
  submitErrorMessage: string;
  home: string;
  externalContent: string;
  stillHere: string;
  confirmStillHere: string;
  secondsRemaining: (seconds: number) => string;
  languageMenu: string;
  currentLanguage: string;
  changeLanguage: string;
  highlights: string;
  previousImage: string;
  nextImage: string;
  selectImage: string;
  noMedia: string;
}

export const UI_TRANSLATIONS: Record<KioskLanguage, KioskTranslations> = {
  gl: {
    loading: "Cargando...",
    surveyTitle: "Enquisa de satisfacción",
    kiosk: "Quiosco",
    experienceQuestion: "Como cualificarías a túa experiencia hoxe?",
    experienceHelp: "A túa opinión é moi valiosa para axudarnos a mellorar os nosos servizos.",
    ratings: ["Moi insatisfeito", "Insatisfeito", "Neutral", "Satisfeito", "Moi satisfeito"],
    originQuestion: "De onde nos visitas?",
    originHelp: "Indica o teu lugar de procedencia para axudarnos a coñecer as persoas que nos visitan.",
    spain: "España",
    europe: "Europa",
    america: "América",
    otherCountry: "Outro país",
    postalCodeTitle: "Introduce o teu código postal",
    postalCodeHelp: "Usa o teclado numérico da pantalla para escribir os 5 díxitos.",
    postalCodeError: "O código postal debe ter exactamente 5 números.",
    delete: "Borrar",
    send: "Enviar",
    thanksTitle: "Moitas grazas por participar!",
    thanksMessage: "A túa valoración rexistrouse correctamente e axúdanos a mellorar o servizo.",
    returningHome: "Volvendo á pantalla principal en",
    returnNow: "Volver agora",
    municipalityMissing: "Non se atopou o identificador do municipio.",
    errorTitle: "Erro",
    submitErrorTitle: "Erro ao enviar",
    submitErrorMessage: "Non se puido rexistrar a túa resposta.",
    home: "INICIO",
    externalContent: "Contido externo",
    stillHere: "Segues aí?",
    confirmStillHere: "Si, sigo aquí",
    secondsRemaining: (seconds) =>
      `${seconds} segundo${seconds === 1 ? "" : "s"} restante${seconds === 1 ? "" : "s"}`,
    languageMenu: "Idiomas dispoñibles",
    currentLanguage: "Idioma actual",
    changeLanguage: "Cambiar idioma",
    highlights: "Destacados de Lugo",
    previousImage: "Imaxe anterior",
    nextImage: "Imaxe seguinte",
    selectImage: "Seleccionar imaxe",
    noMedia: "Sen imaxes ou vídeos configurados",
  },
  es: {
    loading: "Cargando...",
    surveyTitle: "Encuesta de satisfacción",
    kiosk: "Kiosco",
    experienceQuestion: "¿Cómo calificarías tu experiencia hoy?",
    experienceHelp: "Tu opinión es muy valiosa para ayudarnos a mejorar nuestros servicios.",
    ratings: ["Muy insatisfecho", "Insatisfecho", "Neutral", "Satisfecho", "Muy satisfecho"],
    originQuestion: "¿De dónde nos visitas?",
    originHelp: "Indica tu lugar de procedencia para ayudarnos a conocer a nuestros visitantes.",
    spain: "España",
    europe: "Europa",
    america: "América",
    otherCountry: "Otro país",
    postalCodeTitle: "Introduce tu código postal",
    postalCodeHelp: "Usa el teclado numérico de pantalla para escribir tus 5 dígitos.",
    postalCodeError: "El código postal debe tener exactamente 5 números.",
    delete: "Borrar",
    send: "Enviar",
    thanksTitle: "¡Muchas gracias por participar!",
    thanksMessage: "Tu valoración ha sido registrada con éxito. Nos ayuda a mejorar el servicio.",
    returningHome: "Volviendo a la pantalla principal en",
    returnNow: "Volver ahora",
    municipalityMissing: "No se encontró el identificador del municipio.",
    errorTitle: "Error",
    submitErrorTitle: "Error al enviar",
    submitErrorMessage: "No se pudo registrar tu respuesta.",
    home: "INICIO",
    externalContent: "Contenido externo",
    stillHere: "¿Sigues ahí?",
    confirmStillHere: "Sí, estoy aquí",
    secondsRemaining: (seconds) =>
      `${seconds} segundo${seconds === 1 ? "" : "s"} restante${seconds === 1 ? "" : "s"}`,
    languageMenu: "Idiomas disponibles",
    currentLanguage: "Idioma actual",
    changeLanguage: "Cambiar idioma",
    highlights: "Destacados de Lugo",
    previousImage: "Imagen anterior",
    nextImage: "Imagen siguiente",
    selectImage: "Seleccionar imagen",
    noMedia: "Sin imágenes o vídeos configurados",
  },
  en: {
    loading: "Loading...",
    surveyTitle: "Satisfaction survey",
    kiosk: "Kiosk",
    experienceQuestion: "How would you rate your experience today?",
    experienceHelp: "Your feedback is very valuable and helps us improve our services.",
    ratings: ["Very dissatisfied", "Dissatisfied", "Neutral", "Satisfied", "Very satisfied"],
    originQuestion: "Where are you visiting us from?",
    originHelp: "Tell us where you are from to help us understand our visitors.",
    spain: "Spain",
    europe: "Europe",
    america: "America",
    otherCountry: "Other country",
    postalCodeTitle: "Enter your postal code",
    postalCodeHelp: "Use the on-screen keypad to enter all 5 digits.",
    postalCodeError: "The postal code must contain exactly 5 numbers.",
    delete: "Delete",
    send: "Send",
    thanksTitle: "Thank you for taking part!",
    thanksMessage: "Your rating has been recorded successfully and helps us improve the service.",
    returningHome: "Returning to the home screen in",
    returnNow: "Return now",
    municipalityMissing: "The municipality identifier could not be found.",
    errorTitle: "Error",
    submitErrorTitle: "Submission error",
    submitErrorMessage: "Your response could not be recorded.",
    home: "HOME",
    externalContent: "External content",
    stillHere: "Are you still there?",
    confirmStillHere: "Yes, I’m here",
    secondsRemaining: (seconds) => `${seconds} second${seconds === 1 ? "" : "s"} remaining`,
    languageMenu: "Available languages",
    currentLanguage: "Current language",
    changeLanguage: "Change language",
    highlights: "Lugo highlights",
    previousImage: "Previous image",
    nextImage: "Next image",
    selectImage: "Select image",
    noMedia: "No images or videos configured",
  },
};

const WEATHER_TRANSLATIONS: Record<
  KioskLanguage,
  Record<
    | "thunderstorm"
    | "drizzle"
    | "rain"
    | "snow"
    | "mist"
    | "clear"
    | "fewClouds"
    | "scatteredClouds"
    | "brokenClouds"
    | "overcastClouds",
    string
  >
> = {
  gl: {
    thunderstorm: "treboada",
    drizzle: "orballo",
    rain: "chuvia",
    snow: "neve",
    mist: "néboa",
    clear: "ceo despexado",
    fewClouds: "poucas nubes",
    scatteredClouds: "nubes dispersas",
    brokenClouds: "intervalos nubrados",
    overcastClouds: "ceo cuberto",
  },
  es: {
    thunderstorm: "tormenta",
    drizzle: "llovizna",
    rain: "lluvia",
    snow: "nieve",
    mist: "niebla",
    clear: "cielo claro",
    fewClouds: "pocas nubes",
    scatteredClouds: "nubes dispersas",
    brokenClouds: "intervalos nubosos",
    overcastClouds: "cielo cubierto",
  },
  en: {
    thunderstorm: "thunderstorm",
    drizzle: "drizzle",
    rain: "rain",
    snow: "snow",
    mist: "mist",
    clear: "clear sky",
    fewClouds: "few clouds",
    scatteredClouds: "scattered clouds",
    brokenClouds: "broken clouds",
    overcastClouds: "overcast clouds",
  },
};

export const getWeatherDescription = (
  weatherCode: number,
  language: KioskLanguage,
  fallback = "",
): string => {
  const weather = WEATHER_TRANSLATIONS[language];
  if (weatherCode >= 200 && weatherCode < 300) return weather.thunderstorm;
  if (weatherCode >= 300 && weatherCode < 400) return weather.drizzle;
  if (weatherCode >= 500 && weatherCode < 600) return weather.rain;
  if (weatherCode >= 600 && weatherCode < 700) return weather.snow;
  if (weatherCode >= 700 && weatherCode < 800) return weather.mist;
  if (weatherCode === 800) return weather.clear;
  if (weatherCode === 801) return weather.fewClouds;
  if (weatherCode === 802) return weather.scatteredClouds;
  if (weatherCode === 803) return weather.brokenClouds;
  if (weatherCode === 804) return weather.overcastClouds;
  return fallback;
};
